import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createClient } from "@/lib/connect";
import { ReportViewService } from "@gen/tracker/v1/reporting_connect";
import type { ReportView, ReportViewDefinition } from "@gen/tracker/v1/reporting_pb";
import { reportingSchemaKeys } from "./reporting-schema-service";

const client = createClient(ReportViewService);

export const reportViewKeys = {
  all: ["report-views"] as const,
  list: () => [...reportViewKeys.all, "list"] as const,
};

/** `surface`: «report» or «campaigns» — one screen's views; empty = all. */
export function useListReportViews(surface = "") {
  return useQuery({
    queryKey: [...reportViewKeys.list(), surface] as const,
    staleTime: 60_000,
    queryFn: async (): Promise<ReportView[]> => {
      const res = await client.listReportViews({ surface });
      return res.items;
    },
  });
}

/**
 * Every view mutation invalidates both the view list and the reporting
 * schema — the schema response embeds presets + the resolved default view id.
 */
function useInvalidateReportViews() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: reportViewKeys.all });
    void qc.invalidateQueries({ queryKey: reportingSchemaKeys.all });
  };
}

export interface CreateReportViewInput {
  name: string;
  definition: ReportViewDefinition;
  /** true → scope=workspace, else a private user view. */
  workspaceShared: boolean;
}

export function useCreateReportView() {
  const invalidate = useInvalidateReportViews();
  return useMutation({
    mutationFn: async (input: CreateReportViewInput) => {
      const res = await client.createReportView(input);
      return res.view;
    },
    onSuccess: invalidate,
  });
}

export interface UpdateReportViewInput {
  id: string;
  name: string;
  definition: ReportViewDefinition;
}

export function useUpdateReportView() {
  const invalidate = useInvalidateReportViews();
  return useMutation({
    mutationFn: async (input: UpdateReportViewInput) => {
      const res = await client.updateReportView(input);
      return res.view;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteReportView() {
  const invalidate = useInvalidateReportViews();
  return useMutation({
    mutationFn: async (id: string) => {
      await client.deleteReportView({ id });
      return id;
    },
    onSuccess: invalidate,
  });
}

/** Stamps the caller's per-user default view. */
export function useSetDefaultReportView() {
  const invalidate = useInvalidateReportViews();
  return useMutation({
    mutationFn: async (id: string) => {
      await client.setDefaultReportView({ id });
      return id;
    },
    onSuccess: invalidate,
  });
}
